import React, { useEffect, useRef } from "react";
import { useParams, Navigate } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import Container from "@material-ui/core/Container";
import Grid from "@material-ui/core/Grid";
import Typography from "@material-ui/core/Typography";
import Button from "@material-ui/core/Button";
import KeyboardArrowDownIcon from "@material-ui/icons/KeyboardArrowDown";
import Fade from "@material-ui/core/Fade";
import companies from "../data/companies";
import NavigationBar from "../Components/NavigationBar";
import About from "../Components/About";
import Skills from "../Components/Skills";
import Projects from "../Components/Projects";
import Education from "../Components/Education";
import Contact from "../Components/Contact";
import LoomVideo from "../Components/LoomVideo";
import { companyLoomVideoUrl } from "../data/siteMedia";
import { getChatConversationId } from "../utils/chatConversation";

const resumeSnippets = [
  {
    title: "Summary",
    text: "Berkley is a full-stack developer with 5+ years of experience in TypeScript, React, Node.js, Python, and database design. He also has Salesforce and Web3/Solana experience plus CPA exam credentials.",
  },
  {
    title: "Accounting and Tax",
    text: "Berkley is CPA-exam-passed, Intuit Bookkeeping certified, and has 4+ years in state and federal tax audit/compliance, QuickBooks, CP2000 reports, deficiency calculations, and taxpayer/client communication.",
  },
  {
    title: "Bookkeeping",
    text: "At Wittmeyer & Associates, he manages finances, prepares income tax returns, and prepares financial statements and variance reports for management review.",
  },
  {
    title: "TaxRise",
    text: "At TaxRise (Aug 2023 to Jul 2024), he built production full-stack apps using React, Node.js/Python APIs, and PostgreSQL/MySQL for thousands of users.",
  },
  {
    title: "SolTrade Systems",
    text: "At SolTrade Systems (Dec 2024 to current), he built automated crypto trading systems with Node.js, Python, and Solana APIs.",
  },
  {
    title: "Solana Mobile Fit",
    text: "For Solana Mobile-style backend work, he has hands-on @solana/web3.js experience, Node.js/Python automation, on-chain transaction workflows, AWS backend services, PostgreSQL/MySQL/MongoDB, Redis listed in skills, Docker listed in skills, and AI-assisted development with Cursor, Claude, and Codex.",
  },
  {
    title: "DLZP Group",
    text: "At DLZP Group (Aug 2025 to current), he designed an AI chatbot with RAG, Python, AWS Lambda, S3 vector buckets, Bedrock, and dynamic system prompts.",
  },
  {
    title: "TEKware",
    text: "At TEKware (Aug 2021 to Jul 2023), he developed SafetyAuditPrep using React, Node.js APIs, MongoDB, and external integrations.",
  },
];

const stopWords = new Set([
  "a",
  "an",
  "and",
  "are",
  "as",
  "at",
  "be",
  "by",
  "for",
  "from",
  "has",
  "he",
  "in",
  "is",
  "it",
  "of",
  "on",
  "or",
  "that",
  "the",
  "to",
  "was",
  "with",
  "what",
  "which",
  "who",
  "his",
  "about",
  "does",
  "do",
  "you",
]);

function normalizeWords(input) {
  return (input || "")
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, " ")
    .split(/\s+/)
    .filter((word) => word && !stopWords.has(word));
}

function createFallbackReply(question) {
  const tokens = normalizeWords(question);
  const ranked = resumeSnippets
    .map((snippet) => {
      const snippetWords = new Set(
        normalizeWords(`${snippet.title} ${snippet.text}`),
      );
      const overlap = tokens.reduce(
        (score, token) => (snippetWords.has(token) ? score + 1 : score),
        0,
      );
      return { snippet, overlap };
    })
    .sort((a, b) => b.overlap - a.overlap)
    .slice(0, 2)
    .filter((item) => item.overlap > 0);

  if (!ranked.length) {
    return "Berkley has 5+ years in full-stack development across React, Node.js, Python, AWS, Salesforce, and database systems. Ask about a specific skill or experience for more details.";
  }

  return ranked.map((item) => `- ${item.snippet.text}`).join("\n");
}

function getHireHighlights(company) {
  return (
    company.hireHighlights || [
      `Directly relevant experience for the ${company.role} role at ${company.name}.`,
      "5+ years building full-stack products with React, Node.js, Python, databases, and cloud services.",
      "Strong business context from finance, tax, automation, AI, and CPA exam credentials.",
    ]
  );
}

function getRoleArticle(role) {
  const trimmedRole = (role || "").trim();
  if (!trimmedRole) {
    return "a";
  }

  return /^[aeiou]/i.test(trimmedRole) ? "an" : "a";
}

const useStyles = makeStyles((theme) => ({
  app: {
    display: "flex",
  },
  remainingApp: {
    width: "100%",
    [theme.breakpoints.down("sm")]: {
      marginLeft: 0,
    },
  },
  homeContainer: {
    background:
      "linear-gradient(135deg, #0A0A0A 0%, #141414 50%, #0A0A0A 100%)",
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    position: "relative",
    overflow: "hidden",
    "&::before": {
      content: '""',
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundImage: `
        radial-gradient(circle at 25% 25%, rgba(0, 255, 65, 0.03) 0%, transparent 50%),
        radial-gradient(circle at 75% 75%, rgba(0, 255, 65, 0.02) 0%, transparent 50%)
      `,
      pointerEvents: "none",
    },
  },
  contentWrapper: {
    position: "relative",
    zIndex: 1,
    textAlign: "center",
    padding: "40px 20px",
  },
  greeting: {
    color: "#B0B0B0",
    fontSize: "1.2rem",
    fontWeight: 400,
    marginBottom: "16px",
    letterSpacing: "0.1em",
    textTransform: "uppercase",
  },
  nameText: {
    fontWeight: 700,
    color: "#FFFFFF",
    fontSize: "clamp(2.5rem, 8vw, 4.5rem)",
    lineHeight: 1.1,
    marginBottom: "24px",
  },
  roleText: {
    color: "#B0B0B0",
    fontSize: "1.3rem",
    fontWeight: 400,
    marginBottom: "24px",
    [theme.breakpoints.down("xs")]: {
      fontSize: "1.1rem",
    },
  },
  roleHighlight: {
    color: "#00FF41",
    fontWeight: 500,
  },
  inlineProjectLink: {
    color: "#FFFFFF",
    backgroundColor: "#4332C8",
    borderRadius: "6px",
    padding: "2px 7px",
    fontWeight: 700,
    textDecoration: "none",
    whiteSpace: "nowrap",
    transition: "box-shadow 0.2s ease",
    "&:hover": {
      color: "#FFFFFF",
      boxShadow: "0 8px 18px rgba(67, 50, 200, 0.34)",
    },
    "&:focus": {
      outline: "2px solid rgba(255, 255, 255, 0.85)",
      outlineOffset: "3px",
    },
  },
  fitBox: {
    maxWidth: "720px",
    margin: "0 auto 28px",
    padding: "20px 22px",
    background: "rgba(16, 16, 16, 0.82)",
    border: "1px solid rgba(0, 255, 65, 0.22)",
    borderRadius: "8px",
    boxShadow: "0 18px 45px rgba(0, 0, 0, 0.22)",
    textAlign: "left",
    [theme.breakpoints.down("xs")]: {
      padding: "18px",
      marginBottom: "24px",
    },
  },
  fitBoxTitle: {
    color: "#FFFFFF",
    fontSize: "0.95rem",
    fontWeight: 700,
    marginBottom: "14px",
    textTransform: "uppercase",
    letterSpacing: "0.08em",
  },
  fitTitleHighlight: {
    color: "#00FF41",
  },
  fitList: {
    listStyle: "none",
    padding: 0,
    margin: 0,
    display: "grid",
    gap: "12px",
  },
  fitItem: {
    color: "#E6E6E6",
    fontSize: "0.95rem",
    lineHeight: 1.5,
    display: "grid",
    gridTemplateColumns: "20px 1fr",
    gap: "10px",
    alignItems: "start",
    "&::before": {
      content: '"✓"',
      color: "#00FF41",
      fontWeight: 700,
      lineHeight: 1.5,
    },
  },
  buttonContainer: {
    display: "flex",
    gap: "16px",
    justifyContent: "center",
    flexWrap: "wrap",
  },
  primaryButton: {
    background: "#00FF41",
    color: "#000000",
    borderRadius: "8px",
    padding: "14px 32px",
    fontWeight: 600,
    textTransform: "none",
    fontSize: "1rem",
    transition: "all 0.3s ease",
    "&:hover": {
      background: "#00CC33",
      transform: "translateY(-2px)",
      boxShadow: "0 10px 30px rgba(0, 255, 65, 0.3)",
    },
  },
  secondaryButton: {
    background: "transparent",
    color: "#FFFFFF",
    borderRadius: "8px",
    padding: "14px 32px",
    fontWeight: 600,
    textTransform: "none",
    fontSize: "1rem",
    border: "1px solid #2A2A2A",
    transition: "all 0.3s ease",
    "&:hover": {
      borderColor: "#00FF41",
      backgroundColor: "rgba(0, 255, 65, 0.1)",
      transform: "translateY(-2px)",
    },
  },
  scrollIndicator: {
    position: "absolute",
    bottom: "40px",
    left: "50%",
    transform: "translateX(-50%)",
    color: "#B0B0B0",
    animation: "$bounce 2s infinite",
    cursor: "pointer",
    transition: "color 0.3s ease",
    "&:hover": {
      color: "#00FF41",
    },
  },
  "@keyframes bounce": {
    "0%, 20%, 50%, 80%, 100%": {
      transform: "translateX(-50%) translateY(0)",
    },
    "40%": {
      transform: "translateX(-50%) translateY(-10px)",
    },
    "60%": {
      transform: "translateX(-50%) translateY(-5px)",
    },
  },
  link: {
    textDecoration: "none",
  },
  cursor: {
    display: "inline-block",
    width: "3px",
    height: "1em",
    backgroundColor: "#00FF41",
    marginLeft: "4px",
    animation: "$blink 1s infinite",
    verticalAlign: "middle",
    transform: "translateY(-1px)",
  },
  "@keyframes blink": {
    "0%, 50%": { opacity: 1 },
    "51%, 100%": { opacity: 0 },
  },
  // Chat styles
  chatLauncher: {
    position: "fixed",
    right: "24px",
    bottom: "24px",
    zIndex: 30,
    background: "#00FF41",
    color: "#000000",
    borderRadius: "999px",
    padding: "12px 18px",
    fontWeight: 700,
    textTransform: "none",
    boxShadow: "0 12px 24px rgba(0, 0, 0, 0.35)",
    border: "1px solid rgba(255, 255, 255, 0.18)",
    "&:hover": {
      background: "#00CC33",
      transform: "translateY(-2px)",
    },
    [theme.breakpoints.down("xs")]: {
      right: "14px",
      bottom: "14px",
      padding: "10px 14px",
      fontSize: "0.85rem",
    },
  },
  chatLauncherAttention: {
    animation: "$attentionShake 2.2s ease-in-out infinite",
  },
  "@keyframes attentionShake": {
    "0%, 70%, 100%": {
      transform: "translateX(0)",
    },
    "72%, 78%, 84%": {
      transform: "translateX(-2px)",
    },
    "75%, 81%, 87%": {
      transform: "translateX(2px)",
    },
  },
  chatWidget: {
    position: "fixed",
    right: "24px",
    bottom: "86px",
    width: "380px",
    maxHeight: "68vh",
    display: "flex",
    flexDirection: "column",
    background: "#161616",
    border: "2px solid rgba(0, 255, 65, 0.82)",
    borderRadius: "12px",
    boxShadow:
      "0 0 0 1px rgba(255, 255, 255, 0.08), 0 0 34px rgba(0, 255, 65, 0.26), 0 28px 70px rgba(0, 0, 0, 0.62)",
    opacity: 0,
    pointerEvents: "none",
    transform: "translateY(14px)",
    transition: "opacity 0.22s ease, transform 0.22s ease",
    overflow: "hidden",
    zIndex: 29,
    [theme.breakpoints.down("xs")]: {
      left: "12px",
      right: "12px",
      width: "auto",
      bottom: "74px",
      maxHeight: "70vh",
    },
  },
  chatWidgetOpen: {
    opacity: 1,
    pointerEvents: "auto",
    transform: "translateY(0)",
  },
  chatHeader: {
    padding: "16px 20px",
    borderBottom: "1px solid rgba(0, 255, 65, 0.45)",
    background: "linear-gradient(135deg, #1F2A22 0%, #121812 100%)",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
  },
  chatHeaderText: {
    paddingRight: "10px",
  },
  chatTitle: {
    color: "#FFFFFF",
    fontWeight: 700,
    fontSize: "1rem",
    textAlign: "left",
    marginBottom: "4px",
  },
  chatSubtitle: {
    color: "#B0B0B0",
    textAlign: "left",
    fontSize: "0.85rem",
  },
  closeButton: {
    minWidth: "32px",
    width: "32px",
    height: "32px",
    color: "#FFFFFF",
    border: "1px solid rgba(0, 255, 65, 0.45)",
    borderRadius: "8px",
    padding: 0,
    fontSize: "1.1rem",
    lineHeight: 1,
    "&:hover": {
      color: "#FFFFFF",
      borderColor: "#00FF41",
      backgroundColor: "rgba(0, 255, 65, 0.08)",
    },
  },
  messageList: {
    padding: "14px 16px",
    display: "flex",
    flexDirection: "column",
    gap: "10px",
    minHeight: "190px",
    maxHeight: "280px",
    overflowY: "auto",
  },
  messageBubble: {
    borderRadius: "12px",
    padding: "10px 12px",
    fontSize: "0.9rem",
    lineHeight: 1.45,
    maxWidth: "90%",
    whiteSpace: "pre-wrap",
    textAlign: "left",
  },
  assistantBubble: {
    backgroundColor: "#242424",
    color: "#F0F0F0",
    border: "1px solid #3A3A3A",
    alignSelf: "flex-start",
  },
  userBubble: {
    backgroundColor: "rgba(0, 255, 65, 0.12)",
    color: "#FFFFFF",
    border: "1px solid rgba(0, 255, 65, 0.35)",
    alignSelf: "flex-end",
  },
  chatInputRow: {
    padding: "12px 16px 16px",
    borderTop: "1px solid #303030",
    display: "flex",
    gap: "10px",
    [theme.breakpoints.down("xs")]: {
      flexDirection: "column",
    },
  },
  chatInput: {
    flex: 1,
    background: "#222222",
    border: "1px solid #454545",
    borderRadius: "10px",
    color: "#FFFFFF",
    outline: "none",
    padding: "12px 14px",
    fontSize: "0.92rem",
    fontFamily: "inherit",
    "&::placeholder": {
      color: "#707070",
    },
    "&:focus": {
      borderColor: "#00FF41",
      boxShadow: "0 0 0 2px rgba(0, 255, 65, 0.15)",
    },
  },
  sendButton: {
    minWidth: "120px",
    background: "#00FF41",
    color: "#000000",
    borderRadius: "10px",
    fontWeight: 700,
    textTransform: "none",
    "&:hover": {
      background: "#00CC33",
    },
    "&:disabled": {
      background: "#1E1E1E",
      color: "#707070",
    },
  },
  loadingText: {
    color: "#B0B0B0",
    fontSize: "0.8rem",
    textAlign: "left",
    padding: "0 16px 8px",
  },
}));

export default function CompanyPage() {
  const { companySlug } = useParams();
  const company = companies[companySlug];
  const classes = useStyles();
  const [fadedIn, setFadedIn] = React.useState(false);
  const [isChatOpen, setIsChatOpen] = React.useState(false);
  const [question, setQuestion] = React.useState("");
  const [isSending, setIsSending] = React.useState(false);
  const [conversationId] = React.useState(() =>
    getChatConversationId(`portfolio-${companySlug || "main"}-chat`),
  );
  const [messages, setMessages] = React.useState([
    {
      role: "assistant",
      content: company
        ? `Hi! I'm Berkley's AI assistant. Ask me anything about why Berkley is a great fit for ${company.role} at ${company.name}.`
        : "Hi, how can I help you?",
    },
  ]);
  const messageListRef = useRef(null);

  useEffect(() => {
    setFadedIn(true);
  }, []);

  useEffect(() => {
    if (messageListRef.current) {
      messageListRef.current.scrollTop = messageListRef.current.scrollHeight;
    }
  }, [messages, isSending, isChatOpen]);

  const sendQuestion = async (forcedQuestion) => {
    if (isSending) return;

    const finalQuestion = (forcedQuestion || question).trim();
    if (!finalQuestion) return;

    const nextMessages = [
      ...messages,
      { role: "user", content: finalQuestion },
    ];
    setMessages(nextMessages);
    setQuestion("");
    setIsSending(true);

    try {
      const response = await fetch("/.netlify/functions/recruiter-chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          conversationId,
          pagePath:
            typeof window !== "undefined" ? window.location.pathname : "/",
          messages: nextMessages,
          companySlug,
        }),
      });

      const payload = await response.json().catch(() => ({}));
      const reply =
        payload && payload.reply
          ? payload.reply
          : createFallbackReply(finalQuestion);

      setMessages((prev) => [...prev, { role: "assistant", content: reply }]);
    } catch (error) {
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: createFallbackReply(finalQuestion) },
      ]);
    } finally {
      setIsSending(false);
    }
  };

  if (!company) {
    return <Navigate to="/" replace />;
  }

  const hireHighlights = getHireHighlights(company);
  const roleArticle = getRoleArticle(company.role);
  const fitVideoUrl = company.fitVideoUrl;

  return (
    <Fade in={fadedIn} {...(fadedIn ? { timeout: 1000 } : {})}>
      <div className={classes.app}>
        <NavigationBar />
        <div className={classes.remainingApp}>
          <Grid item xs={12} className={classes.homeContainer}>
            <span id="Home" style={{ position: "absolute", top: 0 }}></span>
            <Container maxWidth="md" className={classes.contentWrapper}>
              <Typography className={classes.greeting}>
                Hi {company.name}, my name is
              </Typography>
              <Typography variant="h1" className={classes.nameText}>
                Berkley Olmstead
              </Typography>
              <Typography className={classes.roleText}>
                I would love to be considered for the{" "}
                <span className={classes.roleHighlight}>{company.role}</span>{" "}
                role at{" "}
                <span className={classes.roleHighlight}>{company.name}</span>
                {company.applicationProject && (
                  <>
                    . I also built a{" "}
                    <a
                      href={company.applicationProject.url}
                      className={classes.inlineProjectLink}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {company.applicationProject.linkLabel}
                    </a>{" "}
                    to show React, Node.js, MongoDB, and full-stack product
                    skills.
                  </>
                )}
              </Typography>

              {!company.hideIntroVideo && (
                <LoomVideo
                  loomUrl={companyLoomVideoUrl}
                  inline
                  iframeTitle={`Berkley Olmstead intro video for ${company.name}`}
                />
              )}

              <div className={classes.fitBox}>
                {!company.hideFitTitle && (
                  <Typography className={classes.fitBoxTitle}>
                    Why hire Berkley as {roleArticle}{" "}
                    <span className={classes.fitTitleHighlight}>
                      {company.role}
                    </span>{" "}
                    at{" "}
                    <span className={classes.fitTitleHighlight}>
                      {company.name}
                    </span>
                  </Typography>
                )}
                {fitVideoUrl ? (
                  <LoomVideo
                    loomUrl={fitVideoUrl}
                    inline
                    inlineMaxWidth="100%"
                    inlineMargin="0 auto"
                    sectionId={`${companySlug}-fit-video`}
                    iframeTitle={`Berkley Olmstead fit video for ${company.name}`}
                  />
                ) : (
                  <ul className={classes.fitList}>
                    {hireHighlights.slice(0, 3).map((highlight) => (
                      <li key={highlight} className={classes.fitItem}>
                        {highlight}
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div className={classes.buttonContainer}>
                <Button
                  className={classes.primaryButton}
                  onClick={() => setIsChatOpen(true)}
                >
                  Ask AI about Berkley
                </Button>
              </div>
            </Container>

            <a href="#Skills" className={classes.scrollIndicator}>
              <KeyboardArrowDownIcon style={{ fontSize: 40 }} />
            </a>
          </Grid>

          <About company={company} />
          <Skills />
          <Projects />
          <Education />
          <Contact />
        </div>

        {/* Chat Widget */}
        <div
          className={`${classes.chatWidget} ${
            isChatOpen ? classes.chatWidgetOpen : ""
          }`}
        >
          <div className={classes.chatHeader}>
            <div className={classes.chatHeaderText}>
              <Typography className={classes.chatTitle}>
                Ask about Berkley for this role
              </Typography>
            </div>
            <Button
              className={classes.closeButton}
              onClick={() => setIsChatOpen(false)}
              aria-label="Close chat"
            >
              &times;
            </Button>
          </div>

          <div className={classes.messageList} ref={messageListRef}>
            {messages.map((message, index) => (
              <div
                key={`${message.role}-${index}`}
                className={`${classes.messageBubble} ${
                  message.role === "user"
                    ? classes.userBubble
                    : classes.assistantBubble
                }`}
              >
                {message.content}
              </div>
            ))}
          </div>

          {isSending && (
            <Typography className={classes.loadingText}>Thinking...</Typography>
          )}

          <form
            className={classes.chatInputRow}
            onSubmit={(event) => {
              event.preventDefault();
              sendQuestion();
            }}
          >
            <input
              className={classes.chatInput}
              value={question}
              onChange={(event) => setQuestion(event.target.value)}
              placeholder="e.g. Why is Berkley a fit..."
              disabled={isSending}
            />
            <Button
              type="submit"
              className={classes.sendButton}
              disabled={isSending || !question.trim()}
            >
              Ask
            </Button>
          </form>
        </div>

        <Button
          className={`${classes.chatLauncher} ${
            isChatOpen ? "" : classes.chatLauncherAttention
          }`}
          onClick={() => setIsChatOpen((prev) => !prev)}
        >
          {isChatOpen ? "Close Chat" : "Ask AI about Berkley"}
        </Button>
      </div>
    </Fade>
  );
}
